import { XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field, FilterSelect } from "~/components/filter-controls";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { PAYMENT_METHODS, PAYMENT_PROVIDERS, PAYMENT_STATUSES } from "~/lib/enums";
import { useStorefronts } from "~/lib/queries";
import type { TransactionSearch } from "./-search";

/**
 * The filter bar over the transactions table.
 *
 * It holds no filter state of its own: every control reads from the URL and
 * writes back to it. That is what makes a filtered view a link someone can
 * paste into a thread, and it removes the class of bug where the table and the
 * address bar disagree about what is being shown.
 *
 * The two exceptions are the free-text boxes, which keep a local draft so
 * typing does not fire a request per keystroke. They push to the URL on a
 * debounce.
 */
export function TransactionFilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: TransactionSearch;
  /** Patch the URL. Any key set to undefined is dropped from it. */
  onChange: (patch: Partial<TransactionSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  // Enough for every storefront this system has; the API caps a page at 200.
  // The dropdown exists so nobody has to paste a UUID to filter by merchant.
  const { data: storefronts } = useStorefronts({ limit: 200 });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <Field label="Order ID">
        <DebouncedInput
          value={search.order_id ?? ""}
          onCommit={(value) => onChange({ order_id: value || undefined })}
          placeholder="Starts with…"
          className="w-44"
        />
      </Field>

      <Field label="Storefront">
        <FilterSelect
          value={search.storefront_id ?? null}
          onValueChange={(value) => onChange({ storefront_id: value ?? undefined })}
          placeholder="All storefronts"
          className="w-52"
          options={(storefronts?.items ?? []).map((storefront) => ({
            value: storefront.storefront_id,
            label: storefront.storefront_name,
          }))}
        />
      </Field>

      <Field label="Status">
        <FilterSelect
          value={search.payment_status ?? null}
          onValueChange={(value) => onChange({ payment_status: value ?? undefined })}
          placeholder="Any status"
          className="w-40"
          options={PAYMENT_STATUSES.map((status) => ({ value: status, label: status }))}
        />
      </Field>

      <Field label="Method">
        <FilterSelect
          value={search.payment_method ?? null}
          onValueChange={(value) => onChange({ payment_method: value ?? undefined })}
          placeholder="Any method"
          className="w-40"
          options={PAYMENT_METHODS.map((method) => ({ value: method, label: method }))}
        />
      </Field>

      <Field label="Provider">
        <FilterSelect
          value={search.payment_provider ?? null}
          onValueChange={(value) => onChange({ payment_provider: value ?? undefined })}
          placeholder="Any provider"
          className="w-36"
          options={PAYMENT_PROVIDERS.map((provider) => ({ value: provider, label: provider }))}
        />
      </Field>

      <Field label="Source">
        <DebouncedInput
          value={search.source ?? ""}
          onCommit={(value) => onChange({ source: value || undefined })}
          placeholder="Exact match"
          className="w-32"
        />
      </Field>

      {/* The API reads a bare YYYY-MM-DD as UTC and stretches an end date to the
          end of that day, so a single-day range does include that day's orders. */}
      <Field label="From">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {/* is_deleted is an equality filter on the server, not an "include" flag,
          so this shows deleted rows INSTEAD of live ones. The label says so
          rather than implying a union the API cannot express. */}
      <div className="flex h-8 items-center gap-2">
        <Switch
          id="deleted-only"
          checked={search.deleted ?? false}
          onCheckedChange={(checked) => onChange({ deleted: checked || undefined, offset: 0 })}
        />
        <Label htmlFor="deleted-only" className="text-sm font-normal text-muted-foreground">
          Deleted only
        </Label>
      </div>

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
